import { Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { SmartCartService } from '@app/pages/engage/smart-cart/smart-cart.service';
import { TOP_BAR_STARTER_TEMPLATE, TOP_BAR_VARIABLES, TopBarFormShape } from './top-bar.model';

const ALLOWED_LOGO_IMAGE_TYPES = ['image/png', 'image/svg+xml', 'image/jpeg'];
const MAX_LOGO_FILE_SIZE_BYTES = 100 * 1024;

@Component({
  selector: 'smart-cart-form-top-bar-editor',
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss'],
  standalone: false,
})
export class SmartCartFormTopBarEditorComponent {
  @Input({ required: true }) formGroup!: FormGroup<TopBarFormShape>;
  @Input() usageToken = '';
  @Input() smartCartId: number | null = null;

  readonly starterTemplate = TOP_BAR_STARTER_TEMPLATE;
  readonly customCodeVariables = TOP_BAR_VARIABLES;

  readonly allowedLogoImageTypes = ALLOWED_LOGO_IMAGE_TYPES;
  readonly maxLogoFileSizeBytes = MAX_LOGO_FILE_SIZE_BYTES;

  readonly iconSizeOptions: DropdownOption[] = [
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
  ];

  readonly paddingOptions: DropdownOption[] = [
    { label: 'None', value: 'none' },
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
  ];

  selectedLogoFile: File | null = null;
  logoValidationError: string | null = null;
  uploadingLogo = false;

  private readonly destroyRef = inject(DestroyRef);
  private logoUploadSub: Subscription | null = null;

  constructor(private readonly smartCartService: SmartCartService) {}

  onTitleTextChange(html: string): void {
    this.formGroup.controls.title.controls.text.setValue(html);
  }

  onLogoChanged(file: File | null): void {
    this.logoUploadSub?.unsubscribe();
    this.selectedLogoFile = file;
    this.logoValidationError = null;

    if (!file) {
      this.formGroup.controls.title.controls.logoUrl.setValue('');
      this.uploadingLogo = false;
      return;
    }

    if (this.smartCartId === null) {
      this.logoValidationError = 'Save the cart once before uploading a logo.';
      this.selectedLogoFile = null;
      return;
    }

    this.uploadingLogo = true;
    this.logoUploadSub = this.smartCartService
      .uploadLogo(this.smartCartId, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ url }) => {
          this.formGroup.controls.title.controls.logoUrl.setValue(url);
          this.uploadingLogo = false;
        },
        error: (err) => {
          console.error('[Smart Cart] logo upload failed', err);
          this.logoValidationError = 'Upload failed. Please try again.';
          this.selectedLogoFile = null;
          this.uploadingLogo = false;
        },
      });
  }

  onLogoValidationFailed(reason: 'type' | 'size'): void {
    this.selectedLogoFile = null;
    this.logoValidationError =
      reason === 'type' ? 'Unsupported file type. Use PNG, SVG, or JPG.' : 'File too large. Max 100KB.';
  }
}
